import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import StatusBadge from '../../components/StatusBadge'
import {
  downloadSignedArtifact, getEmail, listDocuments, listSignatures,
  type DocumentSummary, type SignatureSummary,
} from '../../lib/portal'

export default function DashboardPage() {
  const [docs, setDocs]   = useState<DocumentSummary[] | null>(null)
  const [sigs, setSigs]   = useState<SignatureSummary[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    listDocuments().then(setDocs).catch(() => setError('Belgeler yüklenemedi.'))
    listSignatures().then(setSigs).catch(() => { /* non-blocking; summary section just hides */ })
  }, [])

  const stats = docs && {
    total:    docs.length,
    signed:   docs.filter(d => d.status === 'Signed').length,
    awaiting: docs.filter(d => d.status === 'AwaitingSignature').length,
    draft:    docs.filter(d => d.status === 'Draft').length,
  }

  return (
    <>
      <Helmet><title>Panel — W.Sign</title><meta name="robots" content="noindex, nofollow" /></Helmet>
      <section className="container-page py-12">
        <div>
          <h1 className="text-3xl font-semibold">Panelim</h1>
          <p className="mt-1 text-sm text-[var(--color-fg-muted)]">{getEmail()}</p>
        </div>

        {stats && (
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard label="Toplam belge" value={stats.total} />
            <StatCard label="İmzalanmış" value={stats.signed} />
            <StatCard label="İmza bekleyen" value={stats.awaiting} />
            <StatCard label="Taslak" value={stats.draft} />
          </div>
        )}

        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/documents/new" className="btn-primary">Metinden belge oluştur</Link>
          <Link to="/documents/upload" className="btn-ghost">Belge yükle</Link>
          <Link to="/verify" className="btn-ghost">İmza doğrula</Link>
        </div>

        {error && <p className="mt-6 text-sm text-red-700">{error}</p>}

        {/* ── Documents ─────────────────────────────────────────────── */}
        <h2 className="mt-10 text-xl font-semibold">Belgelerim</h2>
        {docs === null ? (
          <p className="mt-4 text-sm text-[var(--color-fg-muted)]">Yükleniyor…</p>
        ) : docs.length === 0 ? (
          <div className="card mt-4">
            <p className="text-sm text-[var(--color-fg-muted)]">
              Henüz belgeniz yok. "Metinden belge oluştur" veya "Belge yükle" ile başlayın.
            </p>
          </div>
        ) : (
          <div className="mt-4 overflow-hidden rounded-lg border border-[var(--color-border)]">
            <table className="w-full text-sm">
              <thead className="bg-[var(--color-bg-soft)] text-left text-xs uppercase tracking-wide text-[var(--color-fg-muted)]">
                <tr>
                  <th className="px-4 py-3">Başlık</th>
                  <th className="px-4 py-3">Durum</th>
                  <th className="px-4 py-3">İmza</th>
                  <th className="px-4 py-3">Tarih</th>
                </tr>
              </thead>
              <tbody>
                {docs.map(d => (
                  <tr key={d.id} className="border-t border-[var(--color-border)] hover:bg-[var(--color-bg-soft)]">
                    <td className="px-4 py-3">
                      <Link to={`/documents/${d.id}`} className="font-medium text-[var(--color-accent)] hover:underline">
                        {d.title}
                      </Link>
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={d.status} /></td>
                    <td className="px-4 py-3">{d.signatureCount}</td>
                    <td className="px-4 py-3 text-[var(--color-fg-muted)]">
                      {new Date(d.createdAt).toLocaleDateString('tr-TR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Previous signatures ───────────────────────────────────── */}
        {sigs && sigs.length > 0 && (
          <>
            <h2 className="mt-10 text-xl font-semibold">Önceki imzalar</h2>
            <p className="mt-1 text-sm text-[var(--color-fg-muted)]">
              Belgeleriniz üzerinde tamamlanmış imzalar.
            </p>
            <div className="mt-4 overflow-hidden rounded-lg border border-[var(--color-border)]">
              <table className="w-full text-sm">
                <thead className="bg-[var(--color-bg-soft)] text-left text-xs uppercase tracking-wide text-[var(--color-fg-muted)]">
                  <tr>
                    <th className="px-4 py-3">Belge</th>
                    <th className="px-4 py-3">İmzalayan</th>
                    <th className="px-4 py-3">Tür</th>
                    <th className="px-4 py-3">Biçim</th>
                    <th className="px-4 py-3">Tarih</th>
                    <th className="px-4 py-3">İndir</th>
                  </tr>
                </thead>
                <tbody>
                  {sigs.map((s, i) => (
                    <tr key={i} className="border-t border-[var(--color-border)] hover:bg-[var(--color-bg-soft)]">
                      <td className="px-4 py-3">
                        <Link to={`/documents/${s.documentId}`} className="font-medium text-[var(--color-accent)] hover:underline">
                          {s.documentTitle}
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        {s.signerSubject}
                        {s.kind === 'EmailInvite' && s.recipientEmail && (
                          <span className="block text-xs text-[var(--color-fg-muted)]">{s.recipientEmail}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-[var(--color-fg-muted)]">
                        {s.kind === 'SelfSign' ? 'Kendi imzam' : 'Karşı taraf'}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-[var(--color-fg-dim)]">{s.format}</td>
                      <td className="px-4 py-3 text-[var(--color-fg-muted)]">
                        {new Date(s.signedAt).toLocaleString('tr-TR')}
                      </td>
                      <td className="px-4 py-3">
                        {(() => {
                          // A PAdES signature is a signed PDF → download as .pdf, not the CAdES .imz.
                          const base   = s.documentTitle.replace(/[/\\"]/g, '').trim() || 'belge'
                          const isPades = s.format.startsWith('PAdES')
                          const fileName = isPades ? `${base}-imzali.pdf` : `${base}.imz`
                          return (
                            <button
                              type="button"
                              className="text-xs text-[var(--color-accent)] hover:underline"
                              onClick={() => downloadSignedArtifact(s.documentId, 'imz', fileName, s.signatureRequestId)}
                            >
                              {isPades ? 'İmzalı PDF indir' : 'İmzalı indir (.imz)'}
                            </button>
                          )
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>
    </>
  )
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-soft)] px-4 py-3">
      <p className="text-2xl font-semibold">{value}</p>
      <p className="text-xs text-[var(--color-fg-muted)]">{label}</p>
    </div>
  )
}
